'use client';

import { StudioPrimaryAction } from '@/components/visual-editor/chat/StudioComposerControls';
import creationChrome from '@/components/visual-editor/CreationChrome.module.css';
import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { discardSoulSave, processSoulSaves, useSoulSaveStore } from '@/stores/soulSaveStore';
import { subjectModelsApi } from '@/lib/api/subject-models';
import { EntitlementFailureNotice } from '@/components/subscription/EntitlementFailureNotice';
import { GiftActionLabel } from '@/components/subscription/GiftActionLabel';
import { getFreeGiftRemaining } from '@/lib/gift-action-presentation';
import { toast } from '@/lib/stores/toast-store';

/** Lives above route/modal lifetimes. OS suspension is recovered on visibility. */
export function SoulSaveRuntime() {
  const t = useTranslations('chat');
  const te = useTranslations('entitlements');
  const ownerId = useAuthStore((state) => state.isAuthenticated && state.accessToken ? state.user?.id : undefined);
  const state = useSoulSaveStore();
  const [expanded, setExpanded] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [resumeQuote, setResumeQuote] = useState<{ id: string; ownerId: string; credits: number; freeGiftRemaining: number | null } | null>(null);
  const seenStages = useRef(new Map<string, string>());
  const manualController = useRef<AbortController | null>(null);
  useEffect(() => {
    if (state.ownerId !== ownerId) return;
    for (const job of state.jobs) {
      const previous = seenStages.current.get(job.id);
      if (previous && previous !== 'completed' && job.stage === 'completed') toast.success(t('soulTrainingStarted'));
      if (previous !== job.stage && ['upload_failed', 'rejected', 'submission_unknown'].includes(job.stage)) setExpanded(true);
      seenStages.current.set(job.id, job.stage);
    }
  }, [state.jobs, state.ownerId, ownerId, t]);
  useEffect(() => { if (state.error) setExpanded(true); }, [state.error]);
  useEffect(() => {
    seenStages.current.clear();
    setResumeQuote(null);
    useSoulSaveStore.setState({ ownerId: ownerId ?? null, jobs: [], busy: false, error: null });
    if (!ownerId) return;
    const controller = new AbortController();
    let running = false;
    let enqueued = false;
    async function update() {
      if (running) return;
      running = true;
      try { await processSoulSaves(ownerId!, controller.signal); }
      catch { if (!controller.signal.aborted) toast.error(t('soulSaveUnavailable')); }
      finally {
        running = false;
        if (enqueued && !controller.signal.aborted) { enqueued = false; void update(); }
      }
    }
    const onEnqueued = () => { enqueued = true; if (!running) { enqueued = false; void update(); } };
    const onVisible = () => { if (document.visibilityState === 'visible') void update(); };
    window.addEventListener('dobidobi:soul-save-enqueued', onEnqueued);
    window.addEventListener('online', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(onVisible, 8000);
    void update();
    return () => {
      controller.abort();
      manualController.current?.abort();
      window.clearInterval(timer);
      window.removeEventListener('dobidobi:soul-save-enqueued', onEnqueued);
      window.removeEventListener('online', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [ownerId, t]);

  const jobs = state.ownerId === ownerId ? state.jobs.filter((job) => job.stage !== 'completed') : [];
  if (!ownerId || (!jobs.length && !state.error)) return null;
  const busy = state.busy || actionBusy;
  return (
    <aside className={`fixed bottom-[calc(80px+env(safe-area-inset-bottom))] right-3 z-[310] ${expanded ? 'w-[300px]' : 'w-auto'} max-w-[calc(100vw-24px)] overflow-hidden rounded-xl border border-[#4ABDE9]/30 bg-white shadow-lg`} aria-label={t('soulSaveTasks')}>
      <button type="button" onClick={() => setExpanded((value) => !value)} aria-expanded={expanded} className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left text-sm font-medium text-[#16263C]">
        {t('soulSaveTasks')} ({jobs.length})
        {expanded ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
      </button>
      {expanded && <div className="max-h-[45dvh] space-y-3 overflow-y-auto border-t border-gray-100 p-3">
        {state.error && <p role="alert" className="text-xs text-red-600">{t(state.error)}</p>}
        {jobs.map((job) => (
          <div key={job.id} className="rounded-lg bg-gray-50 p-3">
            <p className="truncate text-sm font-medium text-gray-800">{job.name}</p>
            <p role="status" className="mt-1 text-xs text-gray-500">{job.stage === 'uploading'
              ? t('subjectModelUploadingPhotos', { completed: job.saved, total: job.total })
              : t(job.errorKey || 'soulSaveConfirming')}</p>
            {job.stage === 'rejected' && <>
              <EntitlementFailureNotice failure={job.entitlementFailure} className="mt-2" />
              {job.recoverable && (resumeQuote?.id === job.id && resumeQuote.ownerId === ownerId
                ? <div className="mt-2 space-y-2">
                  <p className="text-xs text-slate-600">{te('resumeSoulPrice', { credits: resumeQuote.credits })}</p>
                  <StudioPrimaryAction disabled={busy} type="button" className={`${creationChrome.action} ${"min-h-9 rounded-lg bg-[#4ABDE9] px-3 text-xs font-semibold text-[#102F3D] disabled:opacity-50"}`} onClick={async () => {
                    setActionBusy(true);
                    const controller = new AbortController();
                    manualController.current = controller;
                    const acceptedCredits = resumeQuote.credits;
                    setResumeQuote(null);
                    try { await processSoulSaves(ownerId, controller.signal, job.id, acceptedCredits); }
                    catch (error) { toast.showError(error, 'generation', { fallbackMessage: t('soulSaveUnavailable') }); }
                    finally { if (manualController.current === controller) manualController.current = null; setActionBusy(false); }
                  }}><GiftActionLabel action={te('confirmSoulResume')} remaining={resumeQuote.freeGiftRemaining} /></StudioPrimaryAction>
                  <button disabled={busy} type="button" className="ml-2 min-h-9 text-xs text-slate-600" onClick={() => setResumeQuote(null)}>{te('cancel')}</button>
                </div>
                : <button disabled={busy} type="button" className="mt-2 min-h-9 text-xs font-medium text-[#2497C0] disabled:opacity-50" onClick={async () => {
                    setActionBusy(true);
                    try {
                      const response = await subjectModelsApi.soulQuote();
                      if (useAuthStore.getState().user?.id !== ownerId) return;
                      if (response.success !== true || response.error || !response.data) throw response.error ?? new Error('soulSaveUnavailable');
                      const quote = response.data;
                      if (!quote.available || !Number.isFinite(quote.credits) || quote.credits < 0) throw new Error('soulSaveUnavailable');
                      setResumeQuote({ id: job.id, ownerId, credits: quote.credits, freeGiftRemaining: getFreeGiftRemaining({
                        available: quote.available, subscriptionRequired: quote.subscription_required,
                        credits: quote.credits, remaining: quote.monthly_gifts_remaining,
                      }) });
                    } catch (error) { toast.showError(error, 'generation', { fallbackMessage: t('soulSaveUnavailable') }); }
                    finally { setActionBusy(false); }
                  }}>{te('refreshSoulQuote')}</button>)}
            </>}
            {job.stage === 'upload_failed' && <button disabled={busy} type="button" className="mt-2 text-xs font-medium text-[#2497C0] disabled:opacity-50" onClick={async () => {
              setActionBusy(true);
              const controller = new AbortController();
              manualController.current = controller;
              try { await processSoulSaves(ownerId, controller.signal, job.id); }
              catch { toast.error(t('soulSaveUnavailable')); }
              finally { if (manualController.current === controller) manualController.current = null; setActionBusy(false); }
            }}>{t('soulResumePhotos')}</button>}
            {['upload_failed', 'rejected'].includes(job.stage) && <button disabled={busy} type="button" aria-label={t('soulDiscardSave')} className="ml-3 mt-2 inline-flex items-center gap-1 text-xs text-gray-500 disabled:opacity-50" onClick={async () => {
              setActionBusy(true);
              try { await discardSoulSave(ownerId, job.id); }
              catch { toast.error(t('soulSaveUnavailable')); }
              finally { setActionBusy(false); }
            }}><X size={12} />{t('soulDiscardSave')}</button>}
          </div>
        ))}
      </div>}
    </aside>
  );
}
